import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Col, Container, Form, Row, Table } from 'react-bootstrap';
import { Box } from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import moment from 'moment';
import branchApi from '../../services/branchapi';
import businessPerformanceReportApi from '../../services/businessPerformanceReportApi';
import Loader from '../loader/loader';
import DynamicBreadcrumb from '../header/breadcrumb';

const currency = (value) => `₹${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const number = (value) => Number(value || 0).toLocaleString('en-IN');
const percentage = (value) => `${Number(value || 0).toFixed(2)}%`;

const defaultFilters = {
  period1From: '2024-06-01',
  period1To: '2025-05-31',
  period2From: '2025-06-01',
  period2To: '2026-05-31',
  branchId: '',
};

const barWidth = (value, max) => {
  if (!max) return '0%';
  return `${Math.max(4, Math.min(100, (Number(value || 0) / max) * 100))}%`;
};

const KpiCard = ({ title, value, subtitle, growth, type = 'amount' }) => {
  const isPositive = Number(growth || 0) >= 0;
  return (
    <Card className="h-100 shadow-sm border-0">
      <Card.Body>
        <div className="text-muted small">{title}</div>
        <h4 className="mb-1">
          {type === 'amount' ? currency(value) : type === 'percent' ? percentage(value) : number(value)}
        </h4>
        {subtitle && <div className="small text-muted">{subtitle}</div>}
        {growth !== undefined && (
          <div className={`small fw-bold mt-2 ${isPositive ? 'text-success' : 'text-danger'}`}>
            {isPositive ? '↑' : '↓'} {percentage(Math.abs(growth))} vs previous period
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

const SimpleBarChart = ({ title, rows, labelKey, valueKey, formatter = number }) => {
  const max = Math.max(...(rows || []).map((row) => Number(row[valueKey] || 0)), 0);

  return (
    <Card className="shadow-sm border-0 mb-3">
      <Card.Body>
        <h6 className="mb-3">{title}</h6>
        {(rows || []).length === 0 && <div className="text-muted small">No data available</div>}
        {(rows || []).map((row, index) => (
          <div key={`${title}-${index}`} className="mb-2">
            <div className="d-flex justify-content-between small mb-1">
              <span className="text-truncate pe-2" title={row[labelKey]}>
                {row[labelKey]}
              </span>
              <b>{formatter(row[valueKey])}</b>
            </div>
            <div style={{ height: 10, background: '#edf2f7', borderRadius: 20, overflow: 'hidden' }}>
              <div
                style={{
                  width: barWidth(row[valueKey], max),
                  height: '100%',
                  background: '#0d6efd',
                  borderRadius: 20,
                }}
              />
            </div>
          </div>
        ))}
      </Card.Body>
    </Card>
  );
};

const ComparisonTable = ({ period1, period2, growth }) => {
  const rows = [
    ['Total Sales Revenue', currency(period1?.summary?.totalSalesRevenue), currency(period2?.summary?.totalSalesRevenue), percentage(growth?.revenueGrowth)],
    ['Total Orders', number(period1?.summary?.totalOrders), number(period2?.summary?.totalOrders), percentage(growth?.orderGrowth)],
    ['Average Order Value', currency(period1?.summary?.averageOrderValue), currency(period2?.summary?.averageOrderValue), percentage(growth?.aovGrowth)],
    ['New Customers', number(period1?.summary?.newCustomersAcquired), number(period2?.summary?.newCustomersAcquired), percentage(growth?.customerGrowth)],
    ['Repeat Customers', number(period1?.summary?.repeatCustomers), number(period2?.summary?.repeatCustomers), percentage(growth?.repeatCustomerGrowth)],
    ['Order Success Rate', percentage(period1?.summary?.orderSuccessRate), percentage(period2?.summary?.orderSuccessRate), `${Number(growth?.successRateChange || 0).toFixed(2)} pts`],
  ];

  return (
    <Card className="shadow-sm border-0 mb-3">
      <Card.Body>
        <h6 className="mb-3">Old vs New Period Comparison</h6>
        <Table responsive bordered hover size="sm" className="mb-0">
          <thead>
            <tr>
              <th>Metric</th>
              <th>Period 1</th>
              <th>Period 2</th>
              <th>Growth</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row[0]}>
                <td>{row[0]}</td>
                <td>{row[1]}</td>
                <td>{row[2]}</td>
                <td className={String(row[3]).includes('-') ? 'text-danger fw-bold' : 'text-success fw-bold'}>
                  {row[3]}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
};

const BusinessPerformanceReport = () => {
  const reportRef = useRef(null);
  const [filters, setFilters] = useState(defaultFilters);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [branchLoading, setBranchLoading] = useState(false);
  const [error, setError] = useState('');
  const [report, setReport] = useState(null);

  const period1Label = `${moment(filters.period1From).format('MMM YYYY')} - ${moment(filters.period1To).format('MMM YYYY')}`;
  const period2Label = `${moment(filters.period2From).format('MMM YYYY')} - ${moment(filters.period2To).format('MMM YYYY')}`;

  const updateFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

  const fetchBranches = async () => {
    setBranchLoading(true);
    try {
      const branchData = await branchApi.getAllBranches({
        limit: 200,
        page: 0,
        status: 1,
        sortModel: [{ field: 'branchName', sort: 'asc' }],
      });
      setBranches(branchData?.data?.result || []);
    } catch (err) {
      console.error('Error fetching branches:', err);
    } finally {
      setBranchLoading(false);
    }
  };

  const validateFilters = () => {
    if (!filters.period1From || !filters.period1To || !filters.period2From || !filters.period2To) {
      return 'Please select all period dates.';
    }

    if (moment(filters.period1From).isAfter(filters.period1To)) {
      return 'Period 1 From date should not be after Period 1 To date.';
    }

    if (moment(filters.period2From).isAfter(filters.period2To)) {
      return 'Period 2 From date should not be after Period 2 To date.';
    }

    return '';
  };

  const fetchReport = async () => {
    const validationError = validateFilters();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setError('');
    setReport(null);

    try {
      const payload = {
        ...filters,
        branchId: filters.branchId || null,
      };

      const response = await businessPerformanceReportApi.getBusinessPerformanceReport(payload);

      if (response?.status !== 'SUCCESS') {
        throw new Error(response?.message || 'Unable to fetch business performance report');
      }

      setReport(response.data);
    } catch (err) {
      console.error('Error fetching business performance report:', err);
      setError(err.message || 'Unable to fetch business performance report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  const exportRows = useMemo(() => {
    if (!report) return [];

    return [
      { id: 1, section: 'Sales Report', metric: 'Total Sales Revenue', period1: report.period1.summary.totalSalesRevenue, period2: report.period2.summary.totalSalesRevenue, growth: report.growth.revenueGrowth },
      { id: 2, section: 'Sales Report', metric: 'Total Orders', period1: report.period1.summary.totalOrders, period2: report.period2.summary.totalOrders, growth: report.growth.orderGrowth },
      { id: 3, section: 'Sales Report', metric: 'Average Order Value', period1: report.period1.summary.averageOrderValue, period2: report.period2.summary.averageOrderValue, growth: report.growth.aovGrowth },
      { id: 4, section: 'Customer Data Analysis', metric: 'Total Customers Registered', period1: report.period1.summary.totalCustomersRegistered, period2: report.period2.summary.totalCustomersRegistered, growth: '' },
      { id: 5, section: 'Customer Data Analysis', metric: 'New Customers Acquired', period1: report.period1.summary.newCustomersAcquired, period2: report.period2.summary.newCustomersAcquired, growth: report.growth.customerGrowth },
      { id: 6, section: 'Customer Data Analysis', metric: 'Active Customers', period1: report.period1.summary.activeCustomers, period2: report.period2.summary.activeCustomers, growth: '' },
      { id: 7, section: 'Customer Data Analysis', metric: 'Repeat Customers', period1: report.period1.summary.repeatCustomers, period2: report.period2.summary.repeatCustomers, growth: report.growth.repeatCustomerGrowth },
      { id: 8, section: 'Customer Data Analysis', metric: 'Repeat Purchase Rate', period1: report.period1.summary.repeatPurchaseRate, period2: report.period2.summary.repeatPurchaseRate, growth: '' },
      { id: 9, section: 'Order Performance', metric: 'Delivered Orders', period1: report.period1.summary.deliveredOrders, period2: report.period2.summary.deliveredOrders, growth: '' },
      { id: 10, section: 'Order Performance', metric: 'Cancelled Orders', period1: report.period1.summary.cancelledOrders, period2: report.period2.summary.cancelledOrders, growth: '' },
      { id: 11, section: 'Order Performance', metric: 'Refund Orders', period1: report.period1.summary.refundOrders, period2: report.period2.summary.refundOrders, growth: '' },
      { id: 12, section: 'Order Performance', metric: 'Order Success Rate', period1: report.period1.summary.orderSuccessRate, period2: report.period2.summary.orderSuccessRate, growth: report.growth.successRateChange },
      { id: 13, section: 'Order Performance', metric: 'Average Delivery Time Minutes', period1: report.period1.summary.averageDeliveryTimeMinutes, period2: report.period2.summary.averageDeliveryTimeMinutes, growth: '' },
      { id: 14, section: 'Order Performance', metric: 'Express Orders', period1: report.period1.summary.expressOrders, period2: report.period2.summary.expressOrders, growth: '' },
      { id: 15, section: 'Order Performance', metric: 'Regular Orders', period1: report.period1.summary.regularOrders, period2: report.period2.summary.regularOrders, growth: '' },
    ];
  }, [report]);

  const exportColumns = [
    { field: 'section', headerName: 'Section', flex: 1 },
    { field: 'metric', headerName: 'Metric', flex: 1 },
    { field: 'period1', headerName: period1Label, flex: 1 },
    { field: 'period2', headerName: period2Label, flex: 1 },
    { field: 'growth', headerName: 'Growth % / Change', flex: 1 },
  ];

  const pathArray = [{ name: 'Business Performance Report', url: null }];

  return (
    <Container fluid className="mt-4 mb-4">
      <DynamicBreadcrumb pathArray={pathArray} />

      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 className="mb-1">Business Performance Report</h5>
          <div className="text-muted small">Select filters and click Generate to load the report.</div>
        </div>
        <Button variant="outline-secondary" onClick={() => window.print()} disabled={!report}>
          Print / Save PDF
        </Button>
      </div>

      <Card className="shadow-sm border-0 mb-3">
        <Card.Body>
          <Form>
            <Row className="g-3">
              <Col md={2}>
                <Form.Label>Period 1 From</Form.Label>
                <Form.Control type="date" value={filters.period1From} onChange={(e) => updateFilter('period1From', e.target.value)} />
              </Col>

              <Col md={2}>
                <Form.Label>Period 1 To</Form.Label>
                <Form.Control type="date" value={filters.period1To} onChange={(e) => updateFilter('period1To', e.target.value)} />
              </Col>

              <Col md={2}>
                <Form.Label>Period 2 From</Form.Label>
                <Form.Control type="date" value={filters.period2From} onChange={(e) => updateFilter('period2From', e.target.value)} />
              </Col>

              <Col md={2}>
                <Form.Label>Period 2 To</Form.Label>
                <Form.Control type="date" value={filters.period2To} onChange={(e) => updateFilter('period2To', e.target.value)} />
              </Col>

              <Col md={2}>
                <Form.Label>Branch</Form.Label>
                <Form.Select value={filters.branchId} onChange={(e) => updateFilter('branchId', e.target.value)} disabled={branchLoading}>
                  <option value="">All Branches</option>
                  {branches.map((branch) => (
                    <option key={branch.branchId} value={branch.branchId}>
                      {branch.branchName}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col md={2} className="d-flex align-items-end">
                <Button className="w-100" onClick={fetchReport} disabled={loading}>
                  {loading ? 'Generating...' : 'Generate'}
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {loading && <Loader />}
      {error && <Alert variant="danger">{error}</Alert>}

      {!loading && !report && !error && (
        <Alert variant="info">No report loaded yet. Please click Generate to view the business performance report.</Alert>
      )}

      {report && (
        <div ref={reportRef}>
          <Row className="g-3 mb-3">
            <Col md={3}><KpiCard title="Total Sales Revenue" value={report.period2.summary.totalSalesRevenue} subtitle={period2Label} growth={report.growth.revenueGrowth} /></Col>
            <Col md={3}><KpiCard title="Total Orders" value={report.period2.summary.totalOrders} subtitle={period2Label} growth={report.growth.orderGrowth} type="number" /></Col>
            <Col md={3}><KpiCard title="Average Order Value" value={report.period2.summary.averageOrderValue} subtitle={period2Label} growth={report.growth.aovGrowth} /></Col>
            <Col md={3}><KpiCard title="Repeat Purchase Rate" value={report.period2.summary.repeatPurchaseRate} subtitle={period2Label} type="percent" /></Col>
          </Row>

          <ComparisonTable period1={report.period1} period2={report.period2} growth={report.growth} />

          <Row>
            <Col lg={6}><SimpleBarChart title={`Monthly Revenue (${period2Label})`} rows={report.period2.monthlyRevenue} labelKey="month" valueKey="revenue" formatter={currency} /></Col>
            <Col lg={6}><SimpleBarChart title={`Monthly Order Count (${period2Label})`} rows={report.period2.monthlyOrders} labelKey="month" valueKey="orders" formatter={number} /></Col>
          </Row>

          <Row>
            <Col lg={6}><SimpleBarChart title="Branch-wise Sales" rows={report.period2.branchWiseSales} labelKey="branchName" valueKey="revenue" formatter={currency} /></Col>
            <Col lg={6}><SimpleBarChart title="Category-wise Sales" rows={report.period2.categoryWiseSales} labelKey="categoryName" valueKey="revenue" formatter={currency} /></Col>
          </Row>

          <Row>
            <Col lg={6}><SimpleBarChart title="Top Selling Products" rows={report.period2.topSellingProducts} labelKey="productName" valueKey="quantity" formatter={number} /></Col>
            <Col lg={6}><SimpleBarChart title="Customer Growth Month-wise" rows={report.period2.customerGrowthMonthWise} labelKey="month" valueKey="newCustomers" formatter={number} /></Col>
          </Row>

          <Row>
            <Col lg={4}><SimpleBarChart title="Order Status Distribution" rows={report.period2.orderStatusDistribution} labelKey="statusName" valueKey="orders" formatter={number} /></Col>
            <Col lg={4}><SimpleBarChart title="Delivery Performance" rows={report.period2.deliveryPerformance} labelKey="deliveryQuality" valueKey="orders" formatter={number} /></Col>
            <Col lg={4}><SimpleBarChart title="Express vs Regular Orders" rows={report.period2.expressVsRegular} labelKey="deliveryType" valueKey="orders" formatter={number} /></Col>
          </Row>

          <Card className="shadow-sm border-0 mb-3">
            <Card.Body>
              <h6 className="mb-3">Top Customers</h6>
              <Table responsive bordered hover size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Customer</th>
                    <th>Mobile</th>
                    <th>Orders</th>
                    <th>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {(report.period2.topCustomers || []).map((row, index) => (
                    <tr key={`${row.customerId}-${index}`}>
                      <td>{index + 1}</td>
                      <td>{row.customerName}</td>
                      <td>{row.mobile}</td>
                      <td>{number(row.orders)}</td>
                      <td>{currency(row.revenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          <Card className="shadow-sm border-0">
            <Card.Body>
              <h6 className="mb-3">Excel Export Data</h6>
              <Box sx={{ width: '100%' }}>
                <DataGrid
                  autoHeight
                  rows={exportRows}
                  columns={exportColumns}
                  disableRowSelectionOnClick
                  slots={{ toolbar: GridToolbar }}
                  slotProps={{
                    toolbar: {
                      showQuickFilter: true,
                      csvOptions: {
                        fileName: `BusinessPerformance_${moment().format('YYYYMMDD_HHmmss')}`,
                        utf8WithBom: true,
                      },
                      printOptions: { disableToolbarButton: true },
                    },
                  }}
                  pageSizeOptions={[15, 50, 100]}
                  initialState={{ pagination: { paginationModel: { page: 0, pageSize: 15 } } }}
                />
              </Box>
            </Card.Body>
          </Card>
        </div>
      )}
    </Container>
  );
};

export default BusinessPerformanceReport;